import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import Home from './pages/home/Home';
import GeneratedPrompt from './pages/prompt/prompt';
import LoginCallback from './pages/entra/loginCallback';
import Healthcheck from './pages/healthcheck/Healthcheck';
import config from './config/config';
import { RequiredAuth } from './components/SecureRoute/SecureRoute';
import Login from './pages/login/Login';
import { AuthProvider } from './context/Auth.context';
import { FEATURE_FLAGS, isFeatureEnabled } from './utils/featureFlag';
import Loading from './components/Loading/Loading';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';

function App() {
  const navigate = useNavigate();
  const [msalInstance, setMsalInstance] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    const initializeMsal = async () => {
      if (isFeatureEnabled(FEATURE_FLAGS.ENTRA)) {
        setIsInitializing(true);
        
        const instance = new PublicClientApplication({
          auth: config.auth,
          cache: config.cache,
          system: {
            allowNativeBroker: false, // Force web-based auth flow
            navigationClient: {
              navigateInternal: (url, options) => {
                // Handle the MSAL equivalent of restoreOriginalUri - simple navigation
                if (url && typeof url === 'string') {
                  // Extract pathname from full URL if it's a full URL
                  try {
                    const urlObj = new URL(url);
                    const pathname = urlObj.pathname + urlObj.search + urlObj.hash;
                    navigate(pathname);
                  } catch {
                    // If URL parsing fails, treat as relative path
                    navigate(url);
                  }
                } else {
                  navigate('/');
                }
                return Promise.resolve(false); // Prevent default MSAL navigation
              },
              navigateExternal: (url, options) => {
                // Handle external navigation (e.g., logout redirect)
                if (url && typeof url === 'string') {
                  window.location.assign(url);
                  return Promise.resolve(true);
                }
                return Promise.resolve(false);
              }
            }
          }
        });

        try {
          // Initialize MSAL instance properly - await the Promise
          await instance.initialize();

          // Make MSAL instance globally available for token refresh in axios interceptors
          window.msalInstance = instance;
          
          setMsalInstance(instance);
        } catch (error) {
          console.error('MSAL initialization failed:', error);
        } finally {
          setIsInitializing(false);
        }
      }
    };

    initializeMsal();
  }, [navigate]);

  const RenderAuthComponent = () => {
    if (isFeatureEnabled(FEATURE_FLAGS.ENTRA)) {
      // Show loading while MSAL is initializing
      if (isInitializing || !msalInstance) {
        return <Loading />;
      }

      return (
        <>
          <Routes>
            <Route path="/healthcheck" element={<Healthcheck />} />
          </Routes>
          <ErrorBoundary>
            <MsalProvider instance={msalInstance} data-testid="msal-provider">
              <Routes>
                <Route path="/login/callback" element={<LoginCallback loadingElement={Loading} />} />
                <Route path="/" element={<RequiredAuth />}>
                  <Route path="" exact element={<Home />} />
                  <Route path="/prompt/:id/:requestTryNumber" element={<GeneratedPrompt />} />
                </Route>
              </Routes>
            </MsalProvider>
          </ErrorBoundary>
        </>
      );
    }

    // Fallback to basic auth (when Entra isn't enabled)
    return (
      <>
        <Routes>
          <Route path="/healthcheck" exact element={<Healthcheck />} />
        </Routes>
        <AuthProvider>
          <Routes>
            <Route path="/" exact element={<Home />} />
            <Route path="/prompt/:id/:requestTryNumber" element={<GeneratedPrompt />} />
            <Route path="/login" element={<Login />} />
          </Routes>
        </AuthProvider>
      </>
    );
  };

  return <RenderAuthComponent />;
}

export default App;
